import type { Locale } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { match } from "ts-pattern";

import type { SupportedLanguage } from "../config/languages";

let currentLocale: Locale = enUS;

const loadDateFnsLocale = async (
  locale: SupportedLanguage,
): Promise<Locale> => {
  try {
    return await match(locale)
      .with("ja-JP", async () => {
        const module = await import("date-fns/locale/ja");
        return module.ja;
      })
      .with("id-ID", async () => {
        const module = await import("date-fns/locale/id");
        return module.id;
      })
      .with("vn-VN", async () => {
        const module = await import("date-fns/locale/vi");
        return module.vi;
      })
      .with("en-US", () => enUS)
      .with("th-TH", async () => {
        const module = await import("date-fns/locale/th");
        return module.th;
      })
      .with("zh-CN", async () => {
        const module = await import("date-fns/locale/zh-CN");
        return module.zhCN;
      })
      .otherwise(() => {
        throw new Error(`Unsupported locale: ${locale}`);
      });
  } catch (error) {
    if (error instanceof Error && error.message.includes("Failed to load")) {
      console.warn(`Failed to load locale: ${locale}`, error);
      return enUS;
    }
    throw error;
  }
};

export const getDateLocale = async (
  locale: SupportedLanguage,
): Promise<Locale> => {
  if (!locale) {
    return currentLocale;
  }

  const newLocale = await loadDateFnsLocale(locale);
  currentLocale = newLocale;
  return currentLocale;
};

export const getCurrentLocale = (): Locale => currentLocale;

export const loadInitialLocale = async (
  lng: SupportedLanguage,
): Promise<Locale> => getDateLocale(lng);
